import { RENDERER_RUNTIME_CONFIG } from '@shared/runtime-config';

const APP_NAME = import.meta.env.VITE_APP_TARGET === 'desktop'
  ? 'Happily Teams Desktop'
  : 'Happily Teams Web';
const APP_TARGET = import.meta.env.VITE_APP_TARGET === 'desktop' ? 'desktop' : 'web';
const APP_VERSION = RENDERER_RUNTIME_CONFIG.appVersion || 'unknown';
const DEBUG_MODE = import.meta.env.DEV || import.meta.env.VITE_GA_DEBUG === 'true';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const MIN_ENGAGEMENT_TIME_MS = 1;
const MAX_BATCH_SIZE = 25;
const MAX_QUEUE_SIZE = 500;
const MAX_EVENT_AGE_MS = 72 * 60 * 60 * 1000;
// Reserve four GA4 context parameters, optional debug_mode, and the proxy-added
// app_name while staying within GA4's 25-parameter event limit.
const MAX_EVENT_PARAMS = DEBUG_MODE ? 19 : 20;
const RETRY_BASE_DELAY_MS = 5_000;
const RETRY_MAX_DELAY_MS = 5 * 60 * 1000;
// Tracked events wait this long for neighbours before a flush, so a burst of
// screen views costs one request rather than one per event.
const FLUSH_DEBOUNCE_MS = 1_000;
const QUEUE_STORAGE_KEY = 'happily.analytics.queue.v1';
const CLIENT_ID_STORAGE_KEY = 'happily.analytics.client-id.v1';
const EVENT_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;
const PARAM_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;
const USER_PROPERTY_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,23}$/;

type AnalyticsValue = string | number | boolean;
type AnalyticsParams = Record<string, AnalyticsValue | null | undefined>;

interface QueuedAnalyticsEvent {
  id: string;
  name: string;
  params: Record<string, AnalyticsValue>;
  timestampMs: number;
}

interface StoredAnalyticsQueue {
  version: 1;
  events: QueuedAnalyticsEvent[];
}

const eventQueue: QueuedAnalyticsEvent[] = [];
let userProperties: Record<string, AnalyticsValue> = getDefaultUserProperties();
let flushPromise: Promise<void> | null = null;
let currentSessionId: number | null = null;
let lastSessionSeed = 0;
let lastActivityTimestampMs: number | null = null;
let analyticsActive = true;
let activeSinceTimestampMs: number | null = Date.now();
let accumulatedEngagementMs = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let queueHydrated = false;
let analyticsInitialized = false;
let clientId: string | null = null;

export function initAnalytics(): void {
  hydrateQueue();

  if (analyticsInitialized) {
    return;
  }

  analyticsInitialized = true;

  if (typeof document !== 'undefined') {
    setAnalyticsActivity(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', () => {
      setAnalyticsActivity(document.visibilityState !== 'hidden');
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => setAnalyticsActivity(true));
    window.addEventListener('blur', () => setAnalyticsActivity(false));
    window.addEventListener('online', () => {
      cancelRetry();
      void flushAnalytics(true);
    });
    window.addEventListener('pagehide', () => {
      void flushAnalytics();
    });
  }

  void flushAnalytics();
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}): void {
  hydrateQueue();

  if (!canTrack()) {
    return;
  }

  if (!EVENT_NAME_PATTERN.test(eventName)) {
    console.warn(`[Analytics] Ignoring invalid GA4 event name: ${eventName}`);
    return;
  }

  const timestampMs = Date.now();
  eventQueue.push({
    id: createEventId(timestampMs),
    name: eventName,
    params: withAnalyticsContext(params, timestampMs),
    timestampMs,
  });

  if (eventQueue.length > MAX_QUEUE_SIZE) {
    eventQueue.splice(0, eventQueue.length - MAX_QUEUE_SIZE);
    console.warn('[Analytics] Queue limit reached; oldest analytics events were discarded');
  }

  persistQueue();
  scheduleFlush();
}

export function setAnalyticsUserProperties(params: AnalyticsParams): void {
  hydrateQueue();

  if (!canTrack()) {
    return;
  }

  userProperties = {
    ...userProperties,
    ...normalizeParams(params, {
      maxEntries: 25,
      maxStringLength: 36,
      namePattern: USER_PROPERTY_NAME_PATTERN,
    }),
  };

  void flushAnalytics();
}

export function setAnalyticsActivity(isActive: boolean, timestampMs: number = Date.now()): void {
  if (analyticsActive === isActive) {
    if (isActive && activeSinceTimestampMs == null) {
      activeSinceTimestampMs = timestampMs;
    }
    return;
  }

  if (analyticsActive) {
    accumulateActiveTime(timestampMs);
  }

  analyticsActive = isActive;
  activeSinceTimestampMs = isActive ? timestampMs : null;

  if (isActive) {
    cancelRetry();
    void flushAnalytics();
  }
}

export async function flushAnalytics(forceRefresh: boolean = false): Promise<void> {
  cancelScheduledFlush();
  hydrateQueue();
  pruneExpiredEvents();

  if (!canTrack() || eventQueue.length === 0) {
    return;
  }

  if (flushPromise) {
    return flushPromise;
  }

  flushPromise = drainAnalyticsQueue(forceRefresh)
    .finally(() => {
      flushPromise = null;
    });

  return flushPromise;
}

export function resetAnalytics(): void {
  eventQueue.length = 0;
  userProperties = getDefaultUserProperties();
  currentSessionId = null;
  lastSessionSeed = 0;
  lastActivityTimestampMs = null;
  accumulatedEngagementMs = 0;
  analyticsActive = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  activeSinceTimestampMs = analyticsActive ? Date.now() : null;
  queueHydrated = true;
  cancelRetry();
  cancelScheduledFlush();
  removeStoredQueue();
}

async function drainAnalyticsQueue(forceRefresh: boolean): Promise<void> {
  let token: string | null;

  try {
    const { getFirebaseIdToken } = await import('@shared/firebase-auth');
    token = await getFirebaseIdToken(forceRefresh);
    if (!token) {
      return;
    }

    let refreshedAfterUnauthorized = forceRefresh;

    while (eventQueue.length > 0) {
      pruneExpiredEvents();
      if (eventQueue.length === 0) {
        return;
      }

      const batch = eventQueue.slice(0, MAX_BATCH_SIZE);
      let response = await postAnalyticsBatch(batch, token);

      if (response.status === 401 && !refreshedAfterUnauthorized) {
        token = await getFirebaseIdToken(true);
        refreshedAfterUnauthorized = true;
        if (!token) {
          return;
        }
        response = await postAnalyticsBatch(batch, token);
      }

      if (!response.ok) {
        const detail = await readResponseError(response);
        if (response.status >= 400 && response.status < 500 && ![401, 403, 408, 429].includes(response.status)) {
          removeDeliveredEvents(batch);
          persistQueue();
          console.warn(
            `[Analytics] Discarded an invalid analytics batch after HTTP ${response.status}`,
            detail,
          );
          continue;
        }
        throw new Error(`HTTP ${response.status}${detail ? `: ${detail}` : ''}`);
      }

      removeDeliveredEvents(batch);
      persistQueue();
      retryAttempt = 0;
      cancelRetry();
    }
  } catch (error) {
    scheduleRetry();
    console.warn('[Analytics] Failed to flush analytics events', error);
  }
}

async function postAnalyticsBatch(
  batch: QueuedAnalyticsEvent[],
  token: string,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), RENDERER_RUNTIME_CONFIG.proxyTimeoutMs);

  try {
    return await fetch(RENDERER_RUNTIME_CONFIG.analyticsProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        appName: APP_NAME,
        clientId: getClientId(),
        appContext: {
          app_version: APP_VERSION,
          app_target: APP_TARGET,
        },
        userProperties,
        events: batch.map(({ name, params, timestampMs }) => ({
          name,
          params,
          timestampMs,
        })),
      }),
      keepalive: true,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
}

function canTrack(): boolean {
  if (!RENDERER_RUNTIME_CONFIG.analyticsProxyUrl || typeof navigator === 'undefined') {
    return false;
  }

  const doNotTrack = (navigator as NavigatorWithDoNotTrack).doNotTrack
    || (globalThis.window as WindowWithDoNotTrack | undefined)?.doNotTrack;
  return doNotTrack !== '1';
}

function getDefaultUserProperties(): Record<string, AnalyticsValue> {
  return {
    current_app_version: APP_VERSION,
    app_target: APP_TARGET,
  };
}

function normalizeParams(
  params: AnalyticsParams,
  options: {
    maxEntries: number;
    maxStringLength: number;
    namePattern: RegExp;
  } = {
    maxEntries: MAX_EVENT_PARAMS,
    maxStringLength: 100,
    namePattern: PARAM_NAME_PATTERN,
  },
): Record<string, AnalyticsValue> {
  const normalized: Record<string, AnalyticsValue> = {};

  for (const [key, value] of Object.entries(params)) {
    if (Object.keys(normalized).length >= options.maxEntries) {
      break;
    }

    if (!options.namePattern.test(key) || value == null) {
      continue;
    }

    if (typeof value === 'string') {
      normalized[key] = value.slice(0, options.maxStringLength);
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      normalized[key] = value;
    } else if (typeof value === 'boolean') {
      normalized[key] = value;
    }
  }

  return normalized;
}

function withAnalyticsContext(
  params: AnalyticsParams,
  timestampMs: number,
): Record<string, AnalyticsValue> {
  const normalized = normalizeParams(params);
  const { sessionId, isNewSession } = ensureSession(timestampMs);

  normalized.app_version = APP_VERSION;
  normalized.app_target = APP_TARGET;
  normalized.session_id = sessionId;
  normalized.engagement_time_msec = consumeEngagementTime(timestampMs, isNewSession);

  if (DEBUG_MODE) {
    normalized.debug_mode = true;
  }

  lastActivityTimestampMs = timestampMs;

  return normalized;
}

function ensureSession(timestampMs: number): { sessionId: number; isNewSession: boolean } {
  const shouldStartNewSession = currentSessionId == null
    || lastActivityTimestampMs == null
    || timestampMs - lastActivityTimestampMs > SESSION_TIMEOUT_MS;

  if (shouldStartNewSession) {
    const timestampSeconds = Math.floor(timestampMs / 1000);
    const nextSeed = Math.max(timestampSeconds, lastSessionSeed + 1);
    currentSessionId = nextSeed;
    lastSessionSeed = nextSeed;
    return {
      sessionId: currentSessionId,
      isNewSession: true,
    };
  }

  return {
    sessionId: currentSessionId as number,
    isNewSession: false,
  };
}

function consumeEngagementTime(timestampMs: number, isNewSession: boolean): number {
  if (isNewSession) {
    accumulatedEngagementMs = 0;
    activeSinceTimestampMs = analyticsActive ? timestampMs : null;
    return MIN_ENGAGEMENT_TIME_MS;
  }

  accumulateActiveTime(timestampMs);
  const engagementTimeMs = Math.max(Math.round(accumulatedEngagementMs), MIN_ENGAGEMENT_TIME_MS);
  accumulatedEngagementMs = 0;
  activeSinceTimestampMs = analyticsActive ? timestampMs : null;
  return engagementTimeMs;
}

function accumulateActiveTime(timestampMs: number): void {
  if (!analyticsActive || activeSinceTimestampMs == null) {
    return;
  }

  accumulatedEngagementMs += Math.max(0, timestampMs - activeSinceTimestampMs);
  activeSinceTimestampMs = timestampMs;
}

function hydrateQueue(): void {
  if (queueHydrated) {
    return;
  }

  queueHydrated = true;
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    const raw = storage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const stored = JSON.parse(raw) as StoredAnalyticsQueue;
    if (stored?.version !== 1 || !Array.isArray(stored.events)) {
      removeStoredQueue();
      return;
    }

    const cutoff = Date.now() - MAX_EVENT_AGE_MS;
    for (const event of stored.events.slice(-MAX_QUEUE_SIZE)) {
      if (
        typeof event?.id === 'string'
        && typeof event?.name === 'string'
        && EVENT_NAME_PATTERN.test(event.name)
        && Number.isFinite(event.timestampMs)
        && event.timestampMs >= cutoff
        && event.params
        && typeof event.params === 'object'
      ) {
        eventQueue.push({
          id: event.id,
          name: event.name,
          params: normalizeParams(event.params, {
            maxEntries: 25,
            maxStringLength: 100,
            namePattern: PARAM_NAME_PATTERN,
          }),
          timestampMs: event.timestampMs,
        });
      }
    }

    persistQueue();
  } catch {
    removeStoredQueue();
  }
}

function persistQueue(): void {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    if (eventQueue.length === 0) {
      storage.removeItem(QUEUE_STORAGE_KEY);
      return;
    }

    const payload: StoredAnalyticsQueue = {
      version: 1,
      events: eventQueue,
    };
    storage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Analytics must never block the product when browser storage is unavailable.
  }
}

function removeStoredQueue(): void {
  try {
    getStorage()?.removeItem(QUEUE_STORAGE_KEY);
  } catch {
    // Ignore storage access failures.
  }
}

function pruneExpiredEvents(): void {
  const cutoff = Date.now() - MAX_EVENT_AGE_MS;
  const originalLength = eventQueue.length;

  for (let index = eventQueue.length - 1; index >= 0; index -= 1) {
    if (eventQueue[index].timestampMs < cutoff) {
      eventQueue.splice(index, 1);
    }
  }

  if (eventQueue.length !== originalLength) {
    persistQueue();
  }
}

function removeDeliveredEvents(batch: QueuedAnalyticsEvent[]): void {
  const deliveredIds = new Set(batch.map((event) => event.id));

  for (let index = eventQueue.length - 1; index >= 0; index -= 1) {
    if (deliveredIds.has(eventQueue[index].id)) {
      eventQueue.splice(index, 1);
    }
  }
}

function getClientId(): string {
  if (clientId) {
    return clientId;
  }

  const storage = getStorage();
  try {
    const stored = storage?.getItem(CLIENT_ID_STORAGE_KEY) || '';
    if (/^\d+\.\d+$/.test(stored)) {
      clientId = stored;
      return clientId;
    }
  } catch {
    // Fall through to a new in-memory identifier.
  }

  const randomPart = createRandomUint32();
  clientId = `${randomPart}.${Math.floor(Date.now() / 1000)}`;

  try {
    storage?.setItem(CLIENT_ID_STORAGE_KEY, clientId);
  } catch {
    // An in-memory identifier still preserves request validity for this app run.
  }

  return clientId;
}

function createRandomUint32(): number {
  try {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0] || 1;
  } catch {
    return Math.max(1, Math.floor(Math.random() * 0xFFFFFFFF));
  }
}

function createEventId(timestampMs: number): string {
  try {
    return `${timestampMs}-${globalThis.crypto.randomUUID()}`;
  } catch {
    return `${timestampMs}-${createRandomUint32()}-${eventQueue.length}`;
  }
}

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

function scheduleRetry(): void {
  if (retryTimer || !canTrack()) {
    return;
  }

  const delay = Math.min(RETRY_BASE_DELAY_MS * (2 ** retryAttempt), RETRY_MAX_DELAY_MS);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushAnalytics(true);
  }, delay);
}

function cancelRetry(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleFlush(): void {
  if (flushTimer) {
    return;
  }

  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushAnalytics();
  }, FLUSH_DEBOUNCE_MS);
}

function cancelScheduledFlush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

async function readResponseError(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: unknown };
    return typeof body?.error === 'string' ? body.error.slice(0, 200) : '';
  } catch {
    return '';
  }
}

type NavigatorWithDoNotTrack = Navigator & { doNotTrack?: string | null };
type WindowWithDoNotTrack = Window & typeof globalThis & { doNotTrack?: string | null };
